/**
 * Covers — one person doing another's job while they are away (owner, 20 Oct
 * 2026: "I want a system like today amit is absent and there is someone else
 * who will transfer the vehicle to the store from factory so i can transfer
 * that specific function to someone else in the office i want sunil to
 * transfer vehicles in factory today till the time amit is back").
 *
 * A cover hands over the covered person's role — their job — in their book,
 * and nothing else: not their person-by-person switches beyond their role's
 * rights, and none of the covering person's own rights in that book when the
 * cover is the only way into it. Sunil, from TVS, covering Amit's PDI in
 * Bright Autozone is Amit's PDI in Bright Autozone and nothing of TVS dispatch
 * there; in TVS he is himself.
 */
import { BOOK_KEYS } from './domain.js';

/** Why a cover ended. */
export const COVER_END_REASONS = ['BY_HAND', 'UNTIL', 'BACK'] as const;
export type CoverEndReason = (typeof COVER_END_REASONS)[number];

export const COVER_END_LABELS: Record<CoverEndReason, string> = {
  BY_HAND: 'Ended by hand',
  UNTIL: 'Its time came',
  BACK: 'Back — marked in',
};

/** A live cover as the session carries it: what the screens need to show the covered work. */
export interface SessionCover {
  id: string;
  /** Whose work: the person away. */
  forUser: { id: string; name: string };
  /** The covered person's book, where the work is done. */
  organizationId: string;
  /** That book's switch key, when it has one. */
  book: string | null;
  /** The covered job: the covered person's role. */
  role: { key: string; name: string };
  /** The covered person's territory, where their job is an area's (an ASM's). */
  asmId: string | null;
  /** The rights the cover holds in that book. */
  permissions: string[];
  /**
   * The cover is the only way into that book: there, the person is the
   * covered job and nothing of their own. Otherwise the covered rights are
   * added to their own in it.
   */
  crossBook: boolean;
  untilAt: string | null;
  endsOnReturn: boolean;
}

/** A cover as the owner's Covers page lists it. */
export interface CoverRow {
  id: string;
  forUser: { id: string; name: string; role: string; book: string };
  byUser: { id: string; name: string; role: string; book: string };
  startsAt: string;
  untilAt: string | null;
  endsOnReturn: boolean;
  note: string | null;
  madeBy: string | null;
  live: boolean;
  endedAt: string | null;
  endedReason: CoverEndReason | null;
  endedBy: string | null;
}

/** A person either side of a cover can be picked from: every active employee, every book. */
export interface CoverPerson {
  id: string;
  name: string;
  username: string | null;
  role: { key: string; name: string };
  book: string;
  organizationId: string;
}

/** The switch key of a book, for the covering person's book switch — null for a book with none. */
export function bookKeyOfOrganization(organizationId: string): string | null {
  return Object.entries(BOOK_KEYS).find(([, id]) => id === organizationId)?.[0] ?? null;
}

/**
 * Whether a cover, as read, is still live at `now`: not ended, and not past
 * its time. (Its ending on the covered person's return is written when they
 * mark in, so a live row already accounts for it.)
 */
export function isCoverLive(cover: { endedAt: Date | string | null; untilAt: Date | string | null }, now: Date = new Date()): boolean {
  if (cover.endedAt) return false;
  return !cover.untilAt || new Date(cover.untilAt).getTime() > now.getTime();
}

/** "until Amit is back", "until 21 Oct, 6:00 pm", "until Amit is back or 21 Oct, 6:00 pm" — the end in words, given a time formatter. */
export function coverUntilWords(
  cover: { untilAt: string | null; endsOnReturn: boolean },
  forName: string,
  formatTime: (iso: string) => string,
): string {
  const back = cover.endsOnReturn ? `until ${forName} is back` : null;
  const at = cover.untilAt ? formatTime(cover.untilAt) : null;
  if (back && at) return `${back} or ${at}, whichever is first`;
  if (back) return back;
  if (at) return `until ${at}`;
  return 'until it is ended';
}
